import { useState, useEffect, useCallback } from 'react'
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, arrayUnion, query, where } from 'firebase/firestore'
import { db } from '../firebase'

/**
 * Manages user's families and current family selection
 */
export function useFamilies(user) {
  const [userFamilies, setUserFamilies] = useState([])
  const [currentFamilyId, setCurrentFamilyId] = useState(null)
  const [loading, setLoading] = useState(true)

  const generateFamilyId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = 'FAM_'
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  // Fetch all families user belongs to, and auto-sync approved access requests
  const fetchUserFamilies = useCallback(async () => {
    if (!user) {
      setUserFamilies([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const membershipRef = doc(db, 'userFamilyMemberships', user.uid)
      const membershipDoc = await getDoc(membershipRef)

      let familyIds = membershipDoc.exists() ? (membershipDoc.data().familyIds || []) : []

      // Auto-sync: check for approved access requests not yet in membership index.
      // This handles the case where an admin approved a request (adding to familyMembers)
      // but couldn't write to this user's userFamilyMemberships (different UID).
      try {
        const requestsRef = collection(db, 'accessRequests')
        const q = query(requestsRef, where('userId', '==', user.uid), where('status', '==', 'approved'))
        const approvedSnap = await getDocs(q)
        const approvedFamilyIds = approvedSnap.docs.map(d => d.data().familyId).filter(Boolean)
        const missingIds = approvedFamilyIds.filter(id => !familyIds.includes(id))

        if (missingIds.length > 0) {
          for (const id of missingIds) {
            familyIds.push(id)
          }
          // Self-sync: user writes to their own doc (allowed by rules)
          await setDoc(membershipRef, { familyIds: arrayUnion(...missingIds) }, { merge: true })
        }
      } catch (err) {
        // Non-critical: approved requests query may fail if no index exists yet
        console.warn('Auto-sync check failed:', err)
      }

      if (familyIds.length === 0) {
        setUserFamilies([])
        setCurrentFamilyId(null)
        setLoading(false)
        return
      }

      const familyPromises = familyIds.map(async (familyId) => {
        const familyRef = doc(db, 'families', familyId)
        const familyDoc = await getDoc(familyRef)
        if (familyDoc.exists()) {
          return { id: familyId, ...familyDoc.data() }
        }
        return null
      })

      const families = (await Promise.all(familyPromises)).filter(Boolean)
      setUserFamilies(families)

      if (families.length > 0) {
        const currentIsValid = families.some(f => f.id === currentFamilyId)
        if (!currentFamilyId || !currentIsValid) {
          setCurrentFamilyId(families[0].id)
        }
      }
    } catch (err) {
      console.error('Error fetching user families:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.uid]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUserFamilies()
  }, [fetchUserFamilies])

  // Create a new family
  const createFamily = async (familyName) => {
    if (!user) return null

    try {
      const familyId = generateFamilyId()
      
      // Create family document
      const familyRef = doc(db, 'families', familyId)
      await setDoc(familyRef, {
        id: familyId,
        name: familyName,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        memberCount: 1
      })

      // Add user to family members
      const memberRef = doc(db, 'familyMembers', familyId)
      await setDoc(memberRef, {
        members: {
          [user.uid]: {
            email: user.email,
            role: 'admin',
            joinedAt: new Date().toISOString()
          }
        }
      })

      // Add family to user's memberships
      const membershipRef = doc(db, 'userFamilyMemberships', user.uid)
      await setDoc(membershipRef, {
        familyIds: arrayUnion(familyId)
      }, { merge: true })

      // Update local state immediately
      const newFamily = {
        id: familyId,
        name: familyName,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        memberCount: 1
      }
      
      setUserFamilies(prev => [...prev, newFamily])
      setCurrentFamilyId(familyId)
      
      // Return family info
      return {
        familyId,
        family: newFamily
      }
    } catch (err) {
      console.error('Error creating family:', err)
      throw err
    }
  }

  // Join a family directly using a family ID
  const joinFamily = async (familyId) => {
    if (!user) return false

    try {
      // Check if family exists
      const familyRef = doc(db, 'families', familyId)
      const familyDoc = await getDoc(familyRef)

      if (!familyDoc.exists()) {
        throw new Error('Family not found. Please check the Family ID.')
      }

      // Check if already a member
      const memberRef = doc(db, 'familyMembers', familyId)
      const memberDoc = await getDoc(memberRef)

      if (memberDoc.exists()) {
        const members = memberDoc.data().members || {}
        if (members[user.uid]) {
          throw new Error('You are already a member of this family.')
        }
      }

      // Add user to family members
      const existingMembers = memberDoc.exists() ? (memberDoc.data().members || {}) : {}
      existingMembers[user.uid] = {
        email: user.email,
        role: 'member',
        joinedAt: new Date().toISOString()
      }

      await setDoc(memberRef, { members: existingMembers }, { merge: true })

      // Add family to user's memberships
      const membershipRef = doc(db, 'userFamilyMemberships', user.uid)
      await setDoc(membershipRef, {
        familyIds: arrayUnion(familyId)
      }, { merge: true })

      // Update family member count (user is now a member, so rule passes)
      await updateDoc(familyRef, {
        memberCount: Object.keys(existingMembers).length
      })

      // Update local state
      const familyData = familyDoc.data()
      const newFamily = {
        id: familyId,
        ...familyData,
        memberCount: Object.keys(existingMembers).length
      }

      setUserFamilies(prev => [...prev, newFamily])
      setCurrentFamilyId(familyId)

      return { familyId, family: newFamily }
    } catch (err) {
      console.error('Error joining family:', err)
      throw err
    }
  }

  return {
    userFamilies,
    currentFamilyId,
    setCurrentFamilyId,
    loading,
    createFamily,
    joinFamily,
    refreshFamilies: fetchUserFamilies
  }
}
